import {
  createFileRoute,
  redirect,
  stripSearchParams,
} from "@tanstack/react-router";
import {
  ClipboardIcon,
  Feather,
  FileTextIcon,
  RefreshCwIcon,
  Scale,
  SettingsIcon,
  TextSearchIcon,
  TriangleAlertIcon,
  X,
  Zap,
} from "lucide-react";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { z } from "zod/v4";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { diffWordsWithSpace } from "diff";
import { HTTPError } from "ky";
import {
  NotEnoughTokensButton,
  PDFDropzone,
  TokenCount,
  VideoTutorialBox,
  provenanceSubmitButtonClassNames,
} from "./_provenance/-components";
import { getProvenanceInput, setProvenanceInput } from "./_provenance/-hooks";
import type { LucideIcon } from "lucide-react";
import type { MouseEventHandler } from "react";
import type { UseMutationOptions } from "@tanstack/react-query";

import type {
  HumanizerResult,
  UnknownProvenanceModel,
} from "@/lib/types/provenance";

import type { HistoryDetailEntry } from "@/lib/queries/detector/history";
import { SubmitterText } from "@/components/ui/submit-button";
import { CoinIcon } from "@/components/icons/coin";
import { humanizeText } from "@/lib/textAnalyze";
import DetectedPercentages from "@/components/text-analyze/DetectedPercentages";
import { Textarea } from "@/components/ui/textarea";
import { modelCoefficientsQueryOptions } from "@/lib/queries/modelCoefficients";
import { userPlanQueryOptions } from "@/lib/queries/payment/userPlan";
import { Card } from "@/components/ui/card";
import {
  totalBalance,
  userBalanceQueryOptions,
} from "@/lib/queries/userBalance";
import { historyDetailQueryOptions } from "@/lib/queries/detector/history";
import { UpgradePlanModalButton } from "@/components/secure-llm/UpgradePlanModalButton";
import { useTokenCount } from "@/lib/queries/tokenCount";
import { Button } from "@/components/ui/button";
import { CopyButton, PasteButton } from "@/components/ui/clipboard-buttons";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLocalStorage } from "@/lib/hooks/localStorage";
import { LoadingSpinner } from "@/components/loading";
import { UnsupportedLanguageDialog } from "@/components/modal/UnsupportedLanguage";
import { searchBoolean } from "@/lib/utils/zod";

const humanizerSearchSchema = z.object({
  history_id: z.string().optional(),
  submitSaved: searchBoolean,
});
export const Route = createFileRoute("/_app/content-humanizer")({
  component: RouteComponent,
  validateSearch: humanizerSearchSchema,
  search: { middlewares: [stripSearchParams({ submitSaved: false })] },
  loaderDeps: ({ search: { history_id } }) => ({ history_id }),
  async loader({ context: { queryClient }, deps: { history_id } }) {
    queryClient.ensureQueryData(modelCoefficientsQueryOptions);
    queryClient.ensureQueryData(userPlanQueryOptions);

    let historyData;
    if (history_id) {
      try {
        historyData = await queryClient.ensureQueryData(
          historyDetailQueryOptions<
            "HUMANIZER",
            {
              task_id: string;
              text: string;
              parent_id: null | string;
              entry_type: "CONTENT_HUMANIZER";
              request_source: "website";
            }
          >(history_id),
        );
      } catch {
        throw redirect({
          to: ".",
          from: Route.fullPath,
          search: (s) => {
            delete s.history_id;
            return s;
          },
        });
      }

      if (
        (historyData as HistoryDetailEntry<UnknownProvenanceModel> | undefined)
          ?.model_name !== "HUMANIZER"
      )
        throw redirect({
          to: ".",
          from: Route.fullPath,
          search: (s) => {
            delete s.history_id;
            return s;
          },
        });
    }

    return {
      historyData,
    };
  },
  staticData: {
    title: "AI Humanizer (BETA)",
  },
  head: () => ({
    meta: [
      {
        name: "description",
        content:
          "Transform AI-written text into smooth, human-like prose with Polygraf's AI Humanizer - strip out AI artifacts for authentic voice and flow, deployable in local or on-premises environments.",
      },
    ],
  }),
});

const options = {
  light: {
    icon: Feather,
    title: "Light",
    description:
      "Minimal edits. Keeps original style and structure. Best for safe, subtle changes.",
    color: "text-blue-500",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-300",
  },
  balanced: {
    icon: Scale,
    title: "Balanced",
    description:
      "Moderate changes. Balanced between natural tone and accuracy. Ideal for general use.",
    color: "text-gray-600",
    bgColor: "bg-gray-50",
    borderColor: "border-gray-300",
  },
  aggressive: {
    icon: Zap,
    title: "Aggressive",
    description:
      "Heavy rewrite. Creative and harder to detect, but may alter meaning.",
    color: "text-gray-600",
    bgColor: "bg-gray-50",
    borderColor: "border-gray-300",
  },
} as const;

const humanizerEditingStyleSchema = z.union([
  z.literal("light"),
  z.literal("balanced"),
  z.literal("aggressive"),
]);
const humanizerEditingStyleStorageKey =
  "polygraf-humanizer-editing-style" as const;

type Stage =
  | {
      stage: "input";
    }
  | { stage: "result"; data: HumanizerResult };

type HumanizeMutationOptions = UseMutationOptions<
  Awaited<ReturnType<typeof humanizeText>>,
  Error,
  Parameters<typeof humanizeText>[0]
>;
function useHumanize(
  options?: Omit<
    Partial<HumanizeMutationOptions>,
    "mutationKey" | "mutationFn"
  >,
) {
  const { onSuccess: customOnSuccess, ...customOpts } = options ?? {};
  const queryClient = useQueryClient();

  const onSuccess = useCallback<
    Exclude<HumanizeMutationOptions["onSuccess"], undefined>
  >(
    async (...params) => {
      await queryClient.invalidateQueries({
        queryKey: userBalanceQueryOptions.queryKey,
      });
      await customOnSuccess?.(...params);
    },
    [queryClient],
  );

  const opts = {
    mutationKey: ["humanize"],
    mutationFn: humanizeText,
    onSuccess,
  } as const satisfies HumanizeMutationOptions;

  return useMutation({ ...opts, ...customOpts });
}

const segmenter = (Intl.Segmenter as unknown)
  ? new Intl.Segmenter("en-US", { granularity: "word" })
  : null;

function countWords(s: string): number {
  if (segmenter) {
    const segments = segmenter.segment(s);
    return [...segments].filter((segment) => segment.isWordLike).length;
  } else {
    return s.split(" ").filter((n) => n != "").length;
  }
}

const WORD_COUNT_LIMIT = 750;

function RouteComponent() {
  const plan = useSuspenseQuery(userPlanQueryOptions);
  const balance = useSuspenseQuery({
    ...userBalanceQueryOptions,
    select: ({ data }) => totalBalance(data),
  });

  const { historyData } = Route.useLoaderData();

  const [modalData, setModalData] = useState<null | { msg: string }>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleEditText = useCallback(() => {
    setModalData(null);
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  }, []);

  const scanText = useHumanize({
    onSuccess(data) {
      setProvenanceInput(data.result.text);
      setStageInfo({
        stage: "result",
        data: data.result,
      });
    },
    onError: async (error) => {
      if (error instanceof HTTPError) {
        if (error.response.status === 422) {
          try {
            const data = await error.response.json();
            if (data.detail.code === "UNSUPPORTED_LANGUAGE") {
              setModalData({
                msg: "Please enter your text in English to continue.",
              });
              return;
            }
          } catch {}
        }
      }
      // Optionally handle other errors here
    },
  });

  const [stageInfo, setStageInfo] = useState<Stage>(() => {
    const data = historyData
      ? historyData.result
      : (scanText.data?.result ?? null);

    if (data)
      return {
        stage: "result",
        data,
      };
    else return { stage: "input" };
  });

  const [text, setText] = useState(() => {
    return historyData?.payload.text_inputs.text ?? "";
  });

  const price = useTokenCount(text, ["HUMANIZER"]);

  const canAfford =
    balance.data === -1 ? true : (price.data ?? Infinity) <= balance.data;

  const reset = useCallback(() => {
    setText("");
    setStageInfo({ stage: "input" });
    scanText.reset();
  }, [setText, scanText.reset]);

  const wordCount = countWords(text);
  const wordCountExceeded = wordCount > WORD_COUNT_LIMIT;

  const [selectedStyle, setSelectedStyle] = useLocalStorage(
    humanizerEditingStyleStorageKey,
    humanizerEditingStyleSchema,
    "light",
  );
  const [openStyleAccordion, setOpenStyleAccordion] = useState(false);

  const submit = useCallback(() => {
    scanText.mutate({
      text,
      options: { style: selectedStyle, entry_type: "CONTENT_HUMANIZER" },
    });
  }, [scanText.mutate, text]);

  const submitSaved = Route.useSearch({ select: (s) => s.submitSaved });
  useEffect(() => {
    if (!submitSaved) return;
    const text = getProvenanceInput();
    setText(text);
    scanText.mutate({
      text,
      options: { style: "balanced", entry_type: "CONTENT_HUMANIZER" },
    });
  }, []);

  return (
    <>
      <UnsupportedLanguageDialog
        open={modalData !== null}
        msg={modalData?.msg}
        onClose={() => setModalData(null)}
        onCancel={() => {
          setText("");
          setModalData(null);
        }}
        onEditText={handleEditText}
      />
      <div className="relative min-h-[calc(100vh-8rem)] w-full rounded-lg border border-gray-200 p-6 dark:border-gray-800 dark:bg-black dark:text-white">
        <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="col-span-2 flex items-center justify-between max-md:flex-col">
            <div>
              <h1 className="text-2xl font-bold">AI Humanizer (Beta)</h1>
              <p>
                Transform AI-generated text into human-like writing that feels
                authentic, bypass spam filters.
              </p>
            </div>
            <div className="flex max-w-xl items-center justify-end gap-2 max-md:w-full max-md:flex-col">
              {/* not when history is present because htere's no input text to regen (with history v1) when showing history data */}
              {stageInfo.stage === "result" && !historyData && (
                <Button
                  disabled={!scanText.isSuccess}
                  className="mt-5 flex h-10 cursor-pointer items-center gap-2 max-md:w-full"
                  onClick={() =>
                    scanText.mutate({
                      text,
                      options: {
                        style: selectedStyle,
                        entry_type: "CONTENT_HUMANIZER",
                      },
                    })
                  }
                >
                  <SubmitterText
                    submitting={scanText.isPending}
                    submittingChildren="Regenerating..."
                  >
                    <RefreshCwIcon className="size-4" />
                    Regenerate
                  </SubmitterText>
                </Button>
              )}
              <Button
                disabled={!scanText.isSuccess}
                variant="outline"
                className="mt-5 flex h-10 cursor-pointer items-center gap-2 max-md:w-full"
                onClick={reset}
              >
                <FileTextIcon className="size-4" />
                <span>Start New</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {stageInfo.stage === "result" ? (
              <HumanizerResultTextArea results={stageInfo.data} input={text} />
            ) : (
              <div className="size-full">
                <span className="m-1 text-sm text-destructive">
                  Max 750 words*
                </span>
                <div className="relative">
                  <Textarea
                    ref={textareaRef}
                    className="min-h-[600px] resize-none pb-15 focus-visible:border-primary focus-visible:ring-primary/20 data-[toomanywords]:border-red-500 data-[toomanywords]:focus-visible:ring-red-500/20"
                    data-toomanywords={wordCountExceeded ? "true" : undefined}
                    aria-disabled={scanText.isSuccess}
                    disabled={scanText.isSuccess}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                  {scanText.isPending && (
                    <div className="absolute inset-0 z-10000000 flex items-center justify-center rounded-lg bg-white/60 backdrop-blur-sm dark:bg-black/60">
                      <LoadingSpinner size={40} />
                    </div>
                  )}
                </div>
                <PasteButton
                  variant="outline"
                  size="sm"
                  className="relative bottom-10 left-2 cursor-pointer items-center gap-2"
                  setString={setText}
                  successChildren="Pasted!"
                >
                  <ClipboardIcon className="size-4" />
                  Paste
                </PasteButton>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {stageInfo.stage === "input" ? (
              <div className="flex h-full flex-col">
                <div className="flex-1 space-y-6">
                  <PDFDropzone onSuccess={setText} />

                  {/* Warning Box */}
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
                    <div className="flex items-start gap-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <TriangleAlertIcon className="mt-0.5 size-4 flex-shrink-0 text-red-500" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs">
                              Humanizer is a beta tool that "humanizes" AI
                              content so that generic AI-detectors and
                              social-media, email, publisher spam bots are less
                              likely to flag it. It does NOT guarantee 100 %
                              undetectability, and misuse may violate our
                              Responsible-AI Policy.
                            </p>
                            <p className="text-xs">
                              Enterprise accounts can control the heat index.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <div className="text-xs text-red-700 dark:text-red-400">
                        <span className="font-medium">Intended Use: </span>
                        Rephrases 30 -60% of AI content to reduce false-positive
                        spam labels. Not intended for deception, cheating, or
                        policy violations. AI traces are reduced, not fully
                        removed. Humanization output varies based on the style
                        you select (Light, Balanced, or Aggressive).
                      </div>
                    </div>
                  </div>

                  <div className="mx-auto w-full max-w-2xl">
                    <Accordion
                      type="single"
                      collapsible
                      className="space-y-2"
                      value={openStyleAccordion ? "editing-options" : ""}
                      onValueChange={(v) =>
                        setOpenStyleAccordion(v === "editing-options")
                      }
                    >
                      <AccordionItem
                        value="editing-options"
                        className="rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-black"
                      >
                        <AccordionTrigger className="border-b border-gray-200 px-4 py-3 hover:no-underline data-[state=open]:border-b-0 dark:border-gray-800">
                          <div className="flex items-center gap-3 text-left">
                            <SettingsIcon className="h-5 w-5 flex-shrink-0 text-gray-600 dark:text-gray-400" />
                            <div>
                              <span className="text-base font-medium text-gray-700 dark:text-gray-200">
                                Selected editing style:{" "}
                                {options[selectedStyle].title}
                              </span>

                              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                Click to change selection
                              </p>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="border-t border-b border-gray-200 dark:border-gray-800">
                          <div className="space-y-2 p-4">
                            {Object.entries(options).map(([key, option]) => {
                              return (
                                <EditingStyleCard
                                  key={key}
                                  icon={option.icon}
                                  isSelected={selectedStyle === key}
                                  title={option.title}
                                  description={option.description}
                                  onClick={() => {
                                    setOpenStyleAccordion(false);
                                    setSelectedStyle(
                                      key as keyof typeof options,
                                    );
                                  }}
                                />
                              );
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>

                    {canAfford ? (
                    plan.data.info?.plan.features.available_services.includes(
                      "HUMANIZER",
                    ) ? (
                      <>
                        <Button
                          className={provenanceSubmitButtonClassNames}
                          disabled={text.trim().length == 0 || wordCountExceeded}
                          onClick={submit}
                        >
                          <SubmitterText
                            submitting={scanText.isPending}
                            submittingChildren="Eliminating AI..."
                          >
                            <TextSearchIcon className="size-5" />
                            {wordCountExceeded
                              ? "Too many words! (Limit 750)"
                              : "Eliminate AI"}
                          </SubmitterText>
                        </Button>
                        {scanText.isPending && (
                          <>
                            <Button
                              variant="outline"
                              className="mt-2 w-full cursor-pointer border-gray-300 py-6 text-gray-600 hover:bg-gray-100 hover:text-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
                              onClick={() => scanText.reset()}
                            >
                              <X className="mr-2 size-5" />
                              Cancel
                            </Button>
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                              If canceled, tokens will still be spent.
                            </p>
                          </>
                        )}
                      </>
                    ) : (
                      <UpgradePlanModalButton />
                    )
                  ) : (
                    <NotEnoughTokensButton />
                  )}
                </div>

                <div className="sticky bottom-0 z-10 mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-black">
                  <div className="flex justify-between">
                    <div>
                      <h2 className="text-sm font-medium text-primary dark:text-gray-500">
                        Word count
                      </h2>
                      <p className="mt-1 text-xl text-gray-800 dark:text-white">
                        {wordCount}
                      </p>
                    </div>
                    <div>
                      <h2 className="text-sm font-medium text-primary dark:text-gray-500">
                        Token count
                      </h2>
                      <p className="mt-1 text-xl text-gray-400">
                        <TokenCount price={price} />
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Card className="rounded-lg border border-gray-200 p-4 dark:border-gray-800">
                  <div className="mb-2">
                    <div className="mb-2 flex items-center justify-between rounded-lg border-2 border-gray-200 bg-[#FAFAFA] p-4 dark:border-gray-800 dark:bg-transparent">
                      <span>Tokens used</span>
                      <div className="flex items-center gap-2">
                        <CoinIcon />
                        <span className="text-lg font-semibold">
                          {historyData ? (
                            historyData.tokens_used
                          ) : (
                            <TokenCount price={price} />
                          )}
                        </span>
                      </div>
                    </div>
                    <div>
                      <EditingStyleCard
                        icon={options[selectedStyle].icon}
                        // isSelected
                        title={options[selectedStyle].title}
                        description={options[selectedStyle].description}
                      />
                    </div>
                  </div>

                  <HumanizedAiDetectionResult results={stageInfo.data} />
                </Card>
              </>
            )}
          </div>
        </div>
        <VideoTutorialBox
          title="New to Polygraf?"
          description="Learn how to use AI Humanizer"
          youtubeUrl="https://youtu.be/1WVh6hypzh4"
        />
      </div>
    </>
  );
}

function EditingStyleCard({
  icon: IconComponent,
  isSelected,
  title,
  description,
  onClick,
}: {
  onClick?: MouseEventHandler<HTMLDivElement>;
  title: string;
  description: string;
  icon: LucideIcon;
  isSelected?: boolean;
}) {
  return (
    <div
      onClick={onClick}
      className={`cursor-pointer rounded-lg border p-3 transition-all duration-200 hover:shadow-sm dark:border-gray-800 dark:bg-gray-900/50 ${
        isSelected
          ? "border-blue-300 bg-blue-50/50 shadow-sm dark:border-blue-500 dark:bg-blue-950/50"
          : "border-gray-200 bg-gray-50 hover:border-gray-300 dark:hover:border-gray-700"
      }`}
    >
      <div className="flex items-start gap-3">
        <IconComponent
          className={`mt-0.5 h-5 w-5 flex-shrink-0 ${isSelected ? "text-blue-500 dark:text-blue-400" : "text-gray-500 dark:text-gray-400"}`}
        />
        <div className="flex-1">
          <div
            className={`mb-1 text-sm font-medium ${isSelected ? "text-blue-600 dark:text-blue-400" : "text-gray-600 dark:text-gray-200"}`}
          >
            {title}
            {isSelected && (
              <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                Selected
              </span>
            )}
          </div>
          <div className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
            {description}
          </div>
        </div>
      </div>
    </div>
  );
}

function HumanizerResultTextArea({
  results,
  input,
}: {
  results: HumanizerResult;
  input: string;
}) {
  const diff = useMemo(
    () =>
      diffWordsWithSpace(input, results.text, {
        intlSegmenter: (Intl.Segmenter as unknown) // in case not supported yet
          ? new Intl.Segmenter("en-US", { granularity: "word" })
          : undefined,
      }),
    [input, results.text],
  );

  return (
    <table className="w-full border-separate border-spacing-0">
      <tbody>
        <tr className="w-1/2 *:w-1/2">
          <th className="px-2 pb-1 font-semibold">
            <div className="flex w-full flex-row items-center">
              <div>Original</div>
              <CopyButton
                size="sm"
                variant="outline"
                className="m-2 ml-auto justify-self-start"
                text={input}
                successChildren="Copied!"
              >
                Copy
              </CopyButton>
            </div>
          </th>
          <th className="px-2 pb-1 text-right font-semibold">
            <div className="flex w-full flex-row-reverse items-center">
              <div>Humanized</div>
              <CopyButton
                size="sm"
                variant="outline"
                className="m-2 mr-auto justify-self-end"
                text={results.text}
                successChildren="Copied!"
              >
                Copy
              </CopyButton>
            </div>
          </th>
        </tr>
        <tr className="w-1/2 *:h-full *:w-1/2 *:border *:border-input">
          <td className="rounded-md rounded-r-none align-top">
            <div className="m-4 text-justify whitespace-pre-wrap">
              {diff
                .filter((h) => !h.added)
                .map((hunk, i) => (
                  <span
                    key={hunk.value + i}
                    className={
                      hunk.removed
                        ? "min-h-4 min-w-[2px] bg-red-500/20 text-red-500"
                        : undefined
                    }
                  >
                    {hunk.value}
                  </span>
                ))}
            </div>
          </td>
          <td className="rounded-md rounded-l-none align-top">
            <div className="m-4 text-justify whitespace-pre-wrap">
              {diff
                .filter((h) => !h.removed)
                .map((hunk, i) => (
                  <span
                    key={hunk.value + i}
                    className={
                      hunk.added
                        ? "min-w-[2px] bg-green-500/20 text-green-500"
                        : undefined
                    }
                  >
                    {hunk.value}
                  </span>
                ))}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function HumanizedAiDetectionResult({ results }: { results: HumanizerResult }) {
  return <DetectedPercentages results={results.source_detection} />;
}
